import type { AdventureSceneDefinition } from '../../domain/types';

export const soldaterboegenScene: AdventureSceneDefinition = {
  id: 'soldaterboegen-scene',
  title: 'Soldaterbøgen',
  historicalPeriod: 'Historisk periode skal verificeres',
  backgroundAssetId: 'soldaterboegen-background',
  musicAssetId: 'soldaterboegen-music',
  ambienceAssetId: 'soldaterboegen-ambience',
  exitLocationId: 'soldaterboegen',
  layers: [
    { id: 'leaves', depth: 20, animation: 'sway' },
    { id: 'soldier-arm', depth: 12, animation: 'carve' },
    { id: 'campfire-smoke', depth: 15, animation: 'flicker' },
  ],
  documentedHistory: [
    'HISTORISK TEKST SKAL REVIEWES: Soldaterbøgen knyttes i lokal fortælling til soldater i Nørreskoven.',
    'Den præcise periode, hændelse og træets nuværende status skal verificeres mod leveret research.',
  ],
  gameplayFiction: [
    'Spillerens møde og samtale med soldaten er opdigtet.',
    'At soldaten bytter sin kniv for en madpakke, er et inventory-puzzle og ikke en historisk påstand.',
  ],
  fictionLabel: 'Fiktiv spilramme baseret på den historiske periode.',
  sourceReferences: [],
  hotspots: [
    {
      id: 'soldier',
      label: 'Soldaten',
      description: 'En soldat sidder koncentreret ved det gamle træ.',
      shape: {
        type: 'polygon',
        points: [
          { x: 780, y: 330 },
          { x: 1080, y: 330 },
          { x: 1110, y: 850 },
          { x: 760, y: 850 },
        ],
      },
      debugColor: '#f5c45e',
      actions: [
        {
          id: 'look-soldier',
          verb: 'look',
          label: 'Se på',
          effects: [
            { type: 'message', text: 'Han arbejder omhyggeligt og ser ud til at kende skoven.' },
          ],
        },
        {
          id: 'talk-soldier-after-knife',
          verb: 'talk',
          label: 'Tal med',
          conditions: [{ type: 'hasItem', itemId: 'austrianSoldierKnife' }],
          effects: [{ type: 'startDialogue', dialogueId: 'soldier-after-knife' }],
        },
        {
          id: 'talk-soldier',
          verb: 'talk',
          label: 'Tal med',
          conditions: [
            { type: 'missingItem', itemId: 'austrianSoldierKnife' },
            {
              type: 'not',
              condition: { type: 'flag', key: 'soldierKnifeAvailable', equals: true },
            },
          ],
          effects: [{ type: 'startDialogue', dialogueId: 'soldier-intro' }],
        },
        {
          id: 'talk-soldier-eating',
          verb: 'talk',
          label: 'Tal med',
          conditions: [
            { type: 'flag', key: 'soldierKnifeAvailable', equals: true },
            { type: 'missingItem', itemId: 'austrianSoldierKnife' },
          ],
          effects: [{ type: 'startDialogue', dialogueId: 'soldier-eating' }],
        },
        {
          id: 'give-lunch-soldier',
          verb: 'give',
          label: 'Giv madpakken',
          itemId: 'packedLunch',
          conditions: [
            { type: 'hasItem', itemId: 'packedLunch' },
            { type: 'selectedItem', itemId: 'packedLunch' },
            {
              type: 'not',
              condition: { type: 'flag', key: 'soldierKnifeAvailable', equals: true },
            },
          ],
          effects: [
            {
              type: 'sequence',
              effects: [
                { type: 'removeItem', itemId: 'packedLunch' },
                { type: 'setFlag', key: 'soldierKnifeAvailable', value: true },
                { type: 'setFlag', key: 'soldierEating', value: true },
                { type: 'playAnimation', animationId: 'soldier-stop-carving' },
                {
                  type: 'message',
                  text: 'Soldaten lægger kniven på jorden og begynder at spise madpakken.',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'tree',
      label: 'Det gamle bøgetræ',
      description: 'Barken bærer mærker, men deres historie skal kildeverificeres.',
      shape: { type: 'rect', x: 1040, y: 130, width: 340, height: 760 },
      debugColor: '#69d4c3',
      actions: [
        {
          id: 'look-tree',
          verb: 'look',
          label: 'Se på',
          effects: [
            {
              type: 'message',
              text: 'Gamle spor løber gennem barken. Hvad de betyder, kræver et historisk kildecheck.',
            },
          ],
        },
        {
          id: 'use-knife-tree',
          verb: 'use',
          label: 'Brug kniven',
          itemId: 'austrianSoldierKnife',
          conditions: [{ type: 'hasItem', itemId: 'austrianSoldierKnife' }],
          effects: [{ type: 'message', text: 'Jeg tror, træet har været udsat for nok knive.' }],
        },
      ],
    },
    {
      id: 'knife',
      label: 'Kniven',
      shape: { type: 'rect', x: 1120, y: 692, width: 190, height: 92 },
      visibleConditions: [{ type: 'missingItem', itemId: 'austrianSoldierKnife' }],
      debugColor: '#fff1bd',
      actions: [
        {
          id: 'look-knife',
          verb: 'look',
          label: 'Se på',
          effects: [{ type: 'message', text: 'En slidt feltkniv med træskæfte.' }],
        },
        {
          id: 'take-knife',
          verb: 'take',
          label: 'Tag',
          conditions: [
            { type: 'flag', key: 'soldierKnifeAvailable', equals: true },
            { type: 'missingItem', itemId: 'austrianSoldierKnife' },
          ],
          unavailableMessage: 'Soldaten siger: “Jeg bruger kniven.”',
          effects: [
            {
              type: 'sequence',
              effects: [
                {
                  type: 'addItem',
                  itemId: 'austrianSoldierKnife',
                  message: 'Kniven er lagt i rygsækken.',
                },
                { type: 'setFlag', key: 'soldierKnifeTaken', value: true },
                { type: 'markPuzzleSolved', puzzleId: 'borrow-soldiers-knife' },
                { type: 'playSound', soundId: 'item-acquired' },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'backpack',
      label: 'Rygsækken',
      shape: { type: 'rect', x: 650, y: 680, width: 190, height: 180 },
      debugColor: '#ef9e6d',
      actions: [
        {
          id: 'look-backpack',
          verb: 'look',
          label: 'Se på',
          effects: [
            { type: 'message', text: 'Den ser tung ud. Jeg lader soldatens ejendele være.' },
          ],
        },
      ],
    },
    {
      id: 'campfire',
      label: 'Det lille bål',
      shape: {
        type: 'polygon',
        points: [
          { x: 350, y: 720 },
          { x: 620, y: 690 },
          { x: 680, y: 930 },
          { x: 300, y: 930 },
        ],
      },
      debugColor: '#ff7b54',
      actions: [
        {
          id: 'look-campfire',
          verb: 'look',
          label: 'Se på',
          effects: [{ type: 'message', text: 'Et lille bål holder skovens fugt på afstand.' }],
        },
      ],
    },
    {
      id: 'exitToMap',
      label: 'Stien tilbage',
      shape: {
        type: 'polygon',
        points: [
          { x: 0, y: 650 },
          { x: 300, y: 630 },
          { x: 390, y: 1080 },
          { x: 0, y: 1080 },
        ],
      },
      debugColor: '#75b9ff',
      actions: [
        {
          id: 'exit-map',
          verb: 'exit',
          label: 'Tilbage til kortet',
          effects: [{ type: 'returnToMap' }],
        },
      ],
    },
  ],
};

export const adventureScenes: Record<string, AdventureSceneDefinition> = {
  [soldaterboegenScene.id]: soldaterboegenScene,
};
